/* global Foundation */

import $ from 'jquery'

const PORTAL_DATA_KEY = 'revealCloseButtonPortal'
const DESTROY_WRAPPED_KEY = '__closeButtonPortalDestroyWrapped'

function closeModal(modalEl) {
  const $modal = $(modalEl)
  const plugin = $modal.data('zfPlugin')
  if (plugin && typeof plugin.close === 'function') {
    plugin.close()
  }
}

function getPortalTarget(modalEl) {
  const overlay = modalEl.closest('.reveal-overlay')
  if (overlay) {
    return overlay
  }

  return document.body
}

function applyModalVariablesToCloseButton(modalEl, closeButtonEl) {
  const modalStyles = window.getComputedStyle(modalEl)

  const revealBgColor = modalStyles.getPropertyValue('--reveal-bg-color') || '#fff'
  const revealTextColor = modalStyles.getPropertyValue('--reveal-text-color') || '#000'
  const closeButtonSize = modalStyles.getPropertyValue('--close-button-size') || '36px'

  closeButtonEl.style.setProperty('--reveal-bg-color', revealBgColor.trim())
  closeButtonEl.style.setProperty('--reveal-text-color', revealTextColor.trim())
  closeButtonEl.style.setProperty('--close-button-size', closeButtonSize.trim())
}

function restoreCloseButton(state) {
  if (!state.isPortaled) {
    return
  }

  const { closeButtonEl } = state

  if (state.clickListener) {
    closeButtonEl.removeEventListener('click', state.clickListener)
  }

  closeButtonEl.classList.remove('reveal-ported-close-button')
  closeButtonEl.style.removeProperty('top')
  closeButtonEl.style.removeProperty('right')
  closeButtonEl.style.removeProperty('left')
  closeButtonEl.style.removeProperty('--reveal-bg-color')
  closeButtonEl.style.removeProperty('--reveal-text-color')
  closeButtonEl.style.removeProperty('--close-button-size')

  const originalParent = state.originalParentEl
  const originalNextSibling = state.originalNextSiblingEl

  if (originalParent && originalParent.isConnected) {
    if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
      originalParent.insertBefore(closeButtonEl, originalNextSibling)
    } else {
      originalParent.appendChild(closeButtonEl)
    }
  } else if (state.modalEl && state.modalEl.isConnected) {
    state.modalEl.appendChild(closeButtonEl)
  }

  state.clickListener = null
  state.isPortaled = false
}

function portalCloseButton(state) {
  const { closeButtonEl } = state

  if (state.isPortaled) {
    return
  }

  applyModalVariablesToCloseButton(state.modalEl, closeButtonEl)

  closeButtonEl.classList.add('reveal-ported-close-button')

  const portalTarget = getPortalTarget(state.modalEl)
  portalTarget.appendChild(closeButtonEl)

  state.clickListener = (event) => {
    event.preventDefault()
    closeModal(state.modalEl)
  }

  closeButtonEl.addEventListener('click', state.clickListener)
  state.isPortaled = true
}

function setupCloseButtonPortal(modalEl) {
  const modalElement = modalEl?.jquery ? modalEl[0] : modalEl
  if (!modalElement) {
    return () => {}
  }

  const closeButtonEl = modalElement.querySelector('.close-button')
  if (!closeButtonEl) {
    return () => {}
  }

  const $modal = $(modalElement)
  const existingData = $modal.data(PORTAL_DATA_KEY)
  if (existingData && typeof existingData.cleanup === 'function') {
    return existingData.cleanup
  }

  const smallMq = window.APP.mq.small

  const state = {
    modalEl: modalElement,
    closeButtonEl,
    smallMq,
    originalParentEl: closeButtonEl.parentNode,
    originalNextSiblingEl: closeButtonEl.nextSibling,
    isPortaled: false,
    isOpen: false,
    clickListener: null,
  }

  const sync = () => {
    if (!state.isOpen || !state.smallMq.matches) {
      restoreCloseButton(state)
      return
    }

    portalCloseButton(state)
  }

  const onOpen = () => {
    state.isOpen = true
    sync()
  }

  const onClosed = () => {
    state.isOpen = false
    restoreCloseButton(state)
  }

  const onMediaChange = () => {
    sync()
  }

  $modal.on('open.zf.reveal.closeButtonPortal', onOpen).on('closed.zf.reveal.closeButtonPortal', onClosed)

  smallMq.addListener(onMediaChange)

  const cleanup = () => {
    $modal.off('open.zf.reveal.closeButtonPortal', onOpen).off('closed.zf.reveal.closeButtonPortal', onClosed)

    smallMq.removeListener(onMediaChange)

    restoreCloseButton(state)
    $modal.removeData(PORTAL_DATA_KEY)
  }

  $modal.data(PORTAL_DATA_KEY, { cleanup })

  if ($modal.hasClass('is-open')) {
    onOpen()
  }

  return cleanup
}

function wrapDestroyWithPortalCleanup(reveal, $modal) {
  if (!reveal || typeof reveal.destroy !== 'function' || reveal[DESTROY_WRAPPED_KEY]) {
    return
  }

  const originalDestroy = reveal.destroy.bind(reveal)

  reveal.destroy = (...args) => {
    const existingData = $modal.data(PORTAL_DATA_KEY)
    if (existingData && typeof existingData.cleanup === 'function') {
      existingData.cleanup()
    }

    return originalDestroy(...args)
  }

  reveal[DESTROY_WRAPPED_KEY] = true
}

function createRevealWithCloseButtonPortal(modalEl, options = {}) {
  const modalElement = modalEl?.jquery ? modalEl[0] : modalEl
  if (!modalElement) {
    return null
  }

  const $modal = $(modalElement)

  let reveal = $modal.data('zfPlugin')
  if (!reveal) {
    reveal = new Foundation.Reveal($modal, options)
  }

  if (!$modal.data(PORTAL_DATA_KEY)) {
    setupCloseButtonPortal(modalElement)
  }

  wrapDestroyWithPortalCleanup(reveal, $modal)

  return reveal
}

export { createRevealWithCloseButtonPortal }
